import { format } from "date-fns";
import React from "react";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { rootStore } from "src/app";
import { Button, Icon, Input, Text } from "src/components";
import { theme } from "src/config";
import { ItemType } from "src/types";
import { getTextColorFromBackgroundColor } from "src/util/util";
import styled from "styled-components";
import { EditDataProps, StatusAvatar } from ".";
import { Row } from "./gridElements";

const TitleRow = styled(Row)`
  background-color: ${theme.color.primary8};
  border-top:       1px solid ${theme.color.primary8};
  border-left:      1px solid ${theme.color.primary8};
  border-right:     1px solid ${theme.color.primary8};
  flex-direction:   row;
  display:          flex;
  justify-content:  space-between;
  align-items:      center;
  padding:          0.6rem;
  padding-left:     1rem;
  height:           auto;
`;

const StyledRow = styled(Row)`
  position: relative;
`

const TitleBarInput = styled(Input)`
  padding-right: 7.2rem;
  border-color:  white;
`

export const TitleBar = (props: EditDataProps): JSX.Element => {
  const { data, onClose } = props;
  const { t, i18n } = useTranslation();
  const [editTitle, setEditTitle] = React.useState<boolean>(data.isNew);
  const [title,     setTitle]     = React.useState<string>(data.title || "");

  const canEdit = props.data.isNew || props.data.canEdit;

  const save = async () => {
    if(!title || !title.length) {
      toast.error(t("please_enter_title"));
      return;
    }

    try {
      await data.set("title", title, true); 
      setEditTitle(false);
      data.setConflict(null);
      data.setEditProp(null);
      toast.success(t("item_saved"));
    } catch (err) {
      console.error("Failed to save item", data, err);
      toast.error(t("failed_save_item"));
      rootStore.itemStores[data.data_type as ItemType].setActive(null);
    }
  }

  const discard = async () => {
    if(data.isNew) {
      onClose();
      toast.success(t("item_discarded"))
      return;
    }
    setTitle(data.conflict || data.title); 
    if(data.conflict) await data.set("title", data.conflict); 
    setEditTitle(false);
    data.setConflict(null);
    data.setEditProp(null);
  }
  
  return (
    <TitleRow>
      <Row style={{alignItems: "center", justifyContent: 'flex-start'}} height="auto">
        <StatusAvatar data={data} />
        <div style={{marginLeft: "1rem"}}>
          <StyledRow height="auto" style={{alignItems:'center'}}>
            {(editTitle ? 
              <TitleBarInput 
                autoFocus 
                placeholder     = {t(`new_item`)}
                value           = {title}
                onEnter         = {save}
                onChange        = {setTitle}
                blurOnEnter     = {false}
                iconId          = {"save"}
                secondaryIconId = {"close"}
                color           = {(data.conflict && data.conflict !== title) ? "red" : getTextColorFromBackgroundColor(theme.color.primary8)}
                icons           = {[
                  {
                    iconId: 'save',
                    action: save,
                  },
                  {
                    iconId: 'close',
                    action: discard,
                  },
                ]}
              />
              :
              <>
                <Text 
                  color      = "white"
                  size       = "large"
                  lineHeight = "2rem"
                  onClick    = {() => {
                    if(canEdit) {
                      setEditTitle(true); 
                      data.setEditProp("title");
                    }
                  }}
                > 
                  <Text size="large" as="span" color="white">#{data.number + ' '}</Text> 
                  {data.title}
                </Text>
                {canEdit && 
                  <Button variant="icon" onClick={() => {setEditTitle(true); data.setEditProp("title")}}>
                    <Icon iconId="edit" cursor="pointer" size="default" color="white" />
                  </Button>
                }
              </>
            )}
          </StyledRow>
          <Text color="white" size="small">{data.created_at && `${t('created_at')} ${format(data.created_at, "yyyy-MM-dd HH:mm")}`}</Text>
        </div>
      </Row>
      <Button variant="icon" onClick={onClose}>
        <Icon iconId="close" cursor="pointer" size="default" color="white" />
      </Button>
    </TitleRow>
  );
}